/* eslint-disable @typescript-eslint/naming-convention */
/**
 * uses `_` as delimiter in plan name (Premium_Yearly for eg)
 * since plan or duration can have longer names delimited by `-`
 * (Enterprise-Tiered_Yearly, Premium_Half-Yearly for eg)
 */
import { PermissionSetType, UserType } from 'api/models';

export enum BillingVersion {
  v1 = 'v1',
  v2 = 'v2',
}

export enum SupportedPlans {
  Professional = 'Professional',
  Premium = 'Premium',
  'Premium-V2' = 'Premium-V2',
  Enterprise = 'Enterprise',
  'Enterprise-V2' = 'Enterprise-V2',
  Essentials = 'Essentials',
  Standard = 'Standard',
}

export enum SupportedPlansType {
  Professional = 'Professional',
  Premium = 'Premium',
  'Premium-V2' = 'Premium_V2',
  Enterprise = 'Enterprise',
  'Enterprise-V2' = 'Enterprise_V2',
  Essentials = 'Essentials',
  Standard = 'Standard',
}

export enum RestrictedPlans {
  'Enterprise-Tiered' = 'Enterprise-Tiered',
  'Enterprise-Tiered-V2' = 'Enterprise-Tiered-V2',
}

export type Plans = SupportedPlans | RestrictedPlans;

enum PlanHierarchy_v1 {
  Professional = 1,
  Premium = 2,
  Enterprise = 3,
  'Enterprise-Tiered' = 3,
}

enum PlanHierarchy_v2 {
  Essentials = 1,
  Standard = 2,
  'Premium-V2' = 3,
  'Enterprise-V2' = 4,
  'Enterprise-Tiered-V2' = 4,
}

export const PlanHierarchy = {
  [BillingVersion.v1]: PlanHierarchy_v1,
  [BillingVersion.v2]: PlanHierarchy_v2,
};

/**
 * TODO: to be fetched from CB via our BE.
 */
export enum YearlyPrice {
  Professional = 19,
  Premium = 49,
  Enterprise = 99,

  Essentials = 19,
  Standard = 49,
  'Premium-V2' = 69,
  'Enterprise-V2' = 99,
}

export enum MonthlyPrice {
  Professional = 29,
  Premium = 59,

  Essentials = 29,
  Standard = 59,
  'Premium-V2' = 79,
}

export enum BillingCycle {
  Monthly = 'monthly',
  Yearly = 'yearly',
  Quarterly = 'quarterly',
  'Half-Yearly' = 'half-yearly',
}

export const AvailablePlans = {
  v1: [
    SupportedPlans.Professional,
    SupportedPlans.Premium,
    SupportedPlans.Enterprise,
  ],
  v2: [
    SupportedPlans.Essentials,
    SupportedPlans.Standard,
    SupportedPlans['Premium-V2'],
    SupportedPlans['Enterprise-V2'],
  ],
};

// Numeric enum to get planId (enums are numberic by default)
export enum PlanIds {
  'Premium_Yearly' = 1,
  'Premium_Quarterly',
  'Premium_Monthly',
  'Professional_Yearly',
  'Professional_Quarterly',
  'Professional_Monthly',
  'Enterprise_Yearly',
  'Premium_Half-Yearly',
  'Professional_Half-Yearly',
  'Enterprise-Tiered_Yearly',
  'Essentials_Yearly',
  'Essentials_Quarterly',
  'Essentials_Monthly',
  'Essentials_Half-Yearly',
  'Standard_Yearly',
  'Standard_Quarterly',
  'Standard_Monthly',
  'Standard_Half-Yearly',
  'Premium-V2_Yearly',
  'Premium-V2_Quarterly',
  'Premium-V2_Monthly',
  'Premium-V2_Half-Yearly',
  'Enterprise-V2_Yearly',
  'Enterprise-Tiered-V2_Yearly',
  'Enterprise_Quarterly',
  'Enterprise-Tiered_Quarterly',
  'Enterprise-V2_Quarterly',
}

// String enums to get plan names.
export enum PlanNames {
  'Premium_Yearly' = 'Premium_Yearly',
  'Premium_Quarterly' = 'Premium_Quarterly',
  'Premium_Monthly' = 'Premium_Monthly',
  'Professional_Yearly' = 'Professional_Yearly',
  'Professional_Quarterly' = 'Professional_Quarterly',
  'Professional_Monthly' = 'Professional_Monthly',
  'Enterprise_Yearly' = 'Enterprise_Yearly',
  'Premium_Half-Yearly' = 'Premium_Half-Yearly',
  'Professional_Half-Yearly' = 'Professional_Half-Yearly',
  'Enterprise-Tiered_Yearly' = 'Enterprise-Tiered_Yearly',
  'Essentials_Yearly' = 'Essentials_Yearly',
  'Essentials_Quarterly' = 'Essentials_Quarterly',
  'Essentials_Monthly' = 'Essentials_Monthly',
  'Essentials_Half-Yearly' = 'Essentials_Half-Yearly',
  'Standard_Yearly' = 'Standard_Yearly',
  'Standard_Quarterly' = 'Standard_Quarterly',
  'Standard_Monthly' = 'Standard_Monthly',
  'Standard_Half-Yearly' = 'Standard_Half-Yearly',
  'Premium-V2_Yearly' = 'Premium-V2_Yearly',
  'Premium-V2_Quarterly' = 'Premium-V2_Quarterly',
  'Premium-V2_Monthly' = 'Premium-V2_Monthly',
  'Premium-V2_Half-Yearly' = 'Premium-V2_Half-Yearly',
  'Enterprise-V2_Yearly' = 'Enterprise-V2_Yearly',
  'Enterprise-Tiered-V2_Yearly' = 'Enterprise-Tiered-V2_Yearly',
  'Enterprise_Quarterly' = 'Enterprise_Quarterly',
  'Enterprise-Tiered_Quarterly' = 'Enterprise-Tiered_Quarterly',
  'Enterprise-V2_Quarterly' = 'Enterprise-V2_Quarterly',
}

export type Plan = {
  planId: PlanIds;
  planName: PlanNames;
  planType: SupportedPlansType;
};

export enum SubscriptionStatus {
  'IN-TRIAL' = 'IN_TRIAL',
  'ACTIVE' = 'ACTIVE',
  'CANCELLED' = 'CANCELLED',
}

export enum CurrencyCode {
  USD,
}

export enum PricingType {
  'LICENSE-BASED' = 'LICENSE_BASED',
  'FIXED-PRICING' = 'FIXED_PRICING',
}

export enum LicenseCapLimit {
  'Professional' = 10,
  'Essentials' = 10,
}

export type NextSubscription = Pick<
  Subscription,
  'licenseQuantity' | 'plan'
> & {
  nextBillingAt: string;
};

export type SubscriptionMeta = {
  paymentMethodActive: boolean;
  planCancellationScheduled: boolean;
  planChangesScheduled: boolean;
  nextSubscription: NextSubscription;
  nextInvoiceTotal: number;
  pricingType: PricingType;
  subscriptionUpdatedAt: string;
  hasPartnerAddon: boolean;
};

export type AddonType = {
  addonId: number;
  addonName: string;
  units: number;
};

export type Subscription = {
  subscriptionId: number;
  subscriptionAlias: string;
  plan: Plan;
  startDate: string;
  endDate: string;
  subscriptionStatus: SubscriptionStatus;
  currency: CurrencyCode;
  licenseQuantity: number;
  availableUserLicenseQuantity: number;
  availablePartnerLicenseQuantity: number;
  partnerLicenseQuantity: number;
  partnerUsedQuantity: number;
  deleted: boolean;
  subscriptionMeta?: SubscriptionMeta;
  usedQuantity: {
    LICENSE: number;
  };
  addons: AddonType[];
};

export type SubscriptionForm = {
  quantity: number;
  billing_cycle: BillingCycle;
};

export type ChangePlanParams = {
  form?: SubscriptionForm;
  subscription: Subscription;
  plan: Plans;
  isReactivation?: boolean;
};

export type ChangePlanRequestPayload = {
  subscriptionId: number;
  hasScheduledChanges?: boolean;
  plan: Pick<Plan, 'planId'> & {
    quantity: number;
  };
};

export type ChangePlanResponse = {
  expires_at: Date;
  updated_at: Date;
  created_at: Date;
  id: string;
  state: string; // should be enum
  embed: boolean;
  type: string; // should be enum
  resource_version: number;
  url: string;
  object: string; // should be enum
};

export type ChangePlanEstimateResponse = {
  amountDue: number;
  amountPaid: number;
  creditsApplied: number;
  currencyCode: CurrencyCode;
  invoiceTotal: number;
};

export type InvoiceDetails = {
  currencyCode: CurrencyCode;
  invoiceTotal: number; // in cents
  primaryEmail: string;
  secondaryEmails: string[];
  startDate: Date;
  endDate: Date;
};

export type PortalSession = {
  expires_at: Date;
  access_url: string;
  created_at: Date;
  id: string;
  customer_id: string;
  token: string;
  status: string;
  object: string;
};

export enum InvoiceStatus {
  'PAID' = 'Paid',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'NOT_PAID' = 'Not_Paid',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'PAYMENT_DUE' = 'Payment_Due',
  'POSTED' = 'Posted',
}

export type Invoice = {
  invoiceId: number;
  invoicedDate: Date;
  description: string;
  seats: number;
  invoiceTotal: number;
  status: InvoiceStatus;
  currencyCode: CurrencyCode;
};

export type InvoiceListResponse = {
  invoices: Invoice[];
};

export type UpgradeTo = {
  plan?: Plans;
  billingCycle?: BillingCycle;
  licenseCount?: number;
};

export type DownloadInvoiceResponse = {
  downloadUrl: string;
  validTill: Date;
};

export type AddMembersRequestPayload = {
  user: {
    emailId: string;
    userType: UserType;
    company: {
      companyId: number;
    };
    role?: {
      roleId?: number;
    };
  };
  permissionSet?: {
    permissionSetType?: PermissionSetType;
    permissionSetId?: number;
  };
}[];

export type AddMembersResponse = AddMembersRequestPayload;
